import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  FormControlLabel,
  Switch,
  Tabs,
  Tab,
  Divider,
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import api from "../services/api";
import { format } from "date-fns";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

interface Bank {
  id: number;
  name: string;
  code: string;
  defaultCurrencyCode: string | null;
}

interface PaymentMethod {
  id: string;
  method: string;
  displayName: string;
  description?: string;
  isActive: boolean;
  priority: number;
  isCrossborder: boolean;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const Orders: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [orders, setOrders] = useState<any[]>([]);
  const [, setMerchants] = useState<any[]>([]);
  const [, setProviders] = useState<any[]>([]);
  const [banks, setBanks] = useState<Bank[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<any>(null);
  const [exchangeRates, setExchangeRates] = useState<any>({});

  // Form fields
  const [formData, setFormData] = useState({
    invId: `TEST_${Date.now()}`,
    type: "DEPOSIT",
    amount: 1000,
    currency: "RUB",
    bank: "",
    bankId: null as number | null,
    method: "",
    cardNumber: "",
    phoneNumber: "",
    accountNumber: "",
    bankIdCode: "",
    recipientFio: "",
    isSbp: false,
    callbackUrl: "https://example.com/callback",
  });

  // API Test fields
  const [apiTestData, setApiTestData] = useState({
    apiKey: "",
    invId: `API_TEST_${Date.now()}`,
    amount: 1000,
    currency: "RUB",
    bank: "",
    bankId: null as number | null,
    method: "",
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    console.log("Banks state updated:", banks);
  }, [banks]);

  const fetchData = async () => {
    try {
      console.log("Starting to fetch data...");

      const ordersData = await api
        .getOrders({ limit: 50 })
        .catch((e) => ({ data: [] }));
      const merchantsData = await api.getMerchants().catch((e) => []);
      const providersData = await api.getProviders().catch((e) => []);
      const ratesData = await api.getExchangeRates().catch((e) => [{}]);

      console.log("Fetching banks...");
      const banksData = await api.getActiveBanks();
      console.log("Banks data received:", banksData);
      console.log("Banks type:", typeof banksData);
      console.log("Is array:", Array.isArray(banksData));
      console.log("Banks length:", banksData?.length);

      console.log("Fetching payment methods...");
      const methodsData = await api
        .getActivePaymentMethods()
        .catch((e) => {
          console.error("Error fetching payment methods:", e);
          return [];
        });

      setOrders(ordersData.data || []);
      setMerchants(Array.isArray(merchantsData) ? merchantsData : []);
      setProviders(Array.isArray(providersData) ? providersData : []);
      setExchangeRates(ratesData[0] || {});

      if (Array.isArray(banksData) && banksData.length > 0) {
        console.log("Setting banks:", banksData);
        setBanks(banksData);
      } else {
        console.error("Banks data is not valid array or empty:", banksData);
        setBanks([]);
      }

      if (Array.isArray(methodsData)) {
        setPaymentMethods(methodsData);
      } else {
        console.error("Payment methods data is not a valid array:", methodsData);
        setPaymentMethods([]);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const handleCreateOrder = async () => {
    setLoading(true);
    try {
      const orderData: any = {
        invId: formData.invId,
        type: formData.type,
        amount: parseFloat(formData.amount.toString()),
        currency: formData.currency,
      };

      // Добавляем только заполненные поля
      if (formData.cardNumber) orderData.cardNumber = formData.cardNumber;
      if (formData.phoneNumber) orderData.phoneNumber = formData.phoneNumber;
      if (formData.accountNumber) orderData.accountNumber = formData.accountNumber;
      if (formData.bankIdCode) orderData.bankIdCode = formData.bankIdCode;
      if (formData.recipientFio) orderData.recipientFio = formData.recipientFio;
      if (formData.isSbp) orderData.isSbp = true;
      if (formData.callbackUrl) orderData.callbackUrl = formData.callbackUrl;
      if (formData.bank) orderData.bank = formData.bank;
      if (formData.bankId) orderData.bankId = formData.bankId;
      if (formData.method) orderData.method = formData.method;

      const result = await api.createOrder(orderData);

      // VP-PM: ссылка на внешнюю платёжную страницу (PaymentMaster) - для ручного
      // теста метода с pageRenderer=PAYMENTMASTER. null для обычных методов.
      const pmUrl: string | null = result.order?.paymentPageUrl ?? null;
      const pmLine = pmUrl ? `\n\nСсылка на оплату (PaymentMaster):\n${pmUrl}` : "";

      if (result.paymentDetails) {
        const details = result.paymentDetails;
        let message = `Ордер создан успешно!\n\nID: ${result.order.invId}\nСтатус: ${result.order.status}\n`;

        if (details.card) {
          message += `\nРеквизиты для оплаты:\nКарта: ${details.card.number}`;
          if (details.card.telefon)
            message += `\nТелефон: ${details.card.telefon}`;
          if (details.card.account_number)
            message += `\nСчет: ${details.card.account_number}`;
          if (details.card.bank_id_code)
            message += `\nБИК: ${details.card.bank_id_code}`;
        }

        if (details.company) {
          message += `\nРеквизиты организации:`;
          if (details.company.organization_name)
            message += `\nОрганизация: ${details.company.organization_name}`;
          if (details.company.inn) message += `\nИНН: ${details.company.inn}`;
          if (details.company.kpp) message += `\nКПП: ${details.company.kpp}`;
          if (details.company.account_number)
            message += `\nСчет: ${details.company.account_number}`;
          if (details.company.bank_id_code)
            message += `\nБИК: ${details.company.bank_id_code}`;
          if (details.company.purpose_of_payment)
            message += `\nНазначение: ${details.company.purpose_of_payment}`;
        }

        if (details.sbp) {
          message += `\n\nДоступна оплата через СБП`;
        }

        alert(message + pmLine);
      } else if (pmUrl) {
        alert(
          `Ордер создан успешно!\nID: ${result.order.invId}\nСтатус: ${result.order.status}${pmLine}`
        );
      } else {
        alert(
          `Ордер создан успешно!\nID: ${result.order.invId}\nСтатус: ${result.order.status}\n\nВнешние реквизиты не получены (провайдер без API интеграции)`
        );
      }

      setFormData({
        ...formData,
        invId: `TEST_${Date.now()}`,
      });
      fetchData();
    } catch (error: any) {
      alert(`Error: ${error.response?.data?.message || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleApiTest = async () => {
    setLoading(true);
    try {
      const orderPayload: any = {
        invId: apiTestData.invId,
        amount: apiTestData.amount,
        currency: apiTestData.currency,
      };
      if (apiTestData.bank) orderPayload.bank = apiTestData.bank;
      if (apiTestData.bankId) orderPayload.bankId = apiTestData.bankId;
      if (apiTestData.method) orderPayload.method = apiTestData.method;

      const response = await api.createMerchantOrder(apiTestData.apiKey, orderPayload);

      alert("API Order created successfully!");
      setApiTestData({
        ...apiTestData,
        invId: `API_TEST_${Date.now()}`,
      });
      fetchData();
    } catch (error: any) {
      alert(`Error: ${error.response?.data?.message || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const calculateFees = (amount: number, type: string) => {
    const merchantFee = amount * 0.02; // 2%
    const providerFee = amount * 0.01; // 1%
    const totalFee = merchantFee + providerFee;
    const netAmount = amount - totalFee;

    return {
      merchantFee,
      providerFee,
      totalFee,
      netAmount,
    };
  };

  const columns: GridColDef[] = [
    { field: "invId", headerName: "Order ID", width: 150 },
    { field: "type", headerName: "Type", width: 100 },
    {
      field: "status",
      headerName: "Status",
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={
            params.value === "COMPLETED"
              ? "success"
              : params.value === "PENDING"
              ? "warning"
              : "default"
          }
          size="small"
        />
      ),
    },
    {
      field: "amount",
      headerName: "Amount",
      width: 120,
      valueGetter: (params) => `${params.row.amount} ${params.row.currency}`,
    },
    {
      field: "merchantFee",
      headerName: "Merchant Fee",
      width: 120,
      valueFormatter: (params) => `${params.value?.toFixed(2)}`,
    },
    {
      field: "providerFee",
      headerName: "Provider Fee",
      width: 120,
      valueFormatter: (params) => `${params.value?.toFixed(2)}`,
    },
    {
      field: "provider",
      headerName: "Provider",
      width: 150,
      renderCell: (params) => params.row.provider?.name || "Not assigned",
    },
    {
      field: "createdAt",
      headerName: "Created",
      width: 180,
      valueFormatter: (params) =>
        format(new Date(params.value), "dd.MM.yyyy HH:mm:ss"),
    },
  ];

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Order Testing
      </Typography>

      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
          <Tab label="Create Order" />
          <Tab label="API Test" />
          <Tab label="Order List" />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                Order Details
              </Typography>

              <TextField
                fullWidth
                label="Order ID"
                value={formData.invId}
                onChange={(e) =>
                  setFormData({ ...formData, invId: e.target.value })
                }
                margin="normal"
              />

              <FormControl fullWidth margin="normal">
                <InputLabel>Type</InputLabel>
                <Select
                  value={formData.type}
                  onChange={(e) =>
                    setFormData({ ...formData, type: e.target.value })
                  }
                >
                  <MenuItem value="DEPOSIT">Deposit</MenuItem>
                  <MenuItem value="WITHDRAWAL">Withdrawal</MenuItem>
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label="Amount"
                type="number"
                value={formData.amount}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    amount: parseFloat(e.target.value),
                  })
                }
                margin="normal"
              />

              <FormControl fullWidth margin="normal">
                <InputLabel>Currency</InputLabel>
                <Select
                  value={formData.currency}
                  onChange={(e) =>
                    setFormData({ ...formData, currency: e.target.value })
                  }
                >
                  <MenuItem value="RUB">RUB</MenuItem>
                  <MenuItem value="USD">USD</MenuItem>
                  <MenuItem value="EUR">EUR</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth margin="normal">
                <InputLabel>Банк (внутрибанк)</InputLabel>
                <Select
                  value={formData.bankId || ""}
                  onChange={(e) => {
                    const selectedBankId = e.target.value as number | "";
                    if (selectedBankId === "") {
                      setFormData({ ...formData, bank: "", bankId: null });
                    } else {
                      const selectedBank = banks.find(b => b.id === selectedBankId);
                      setFormData({
                        ...formData,
                        bank: selectedBank?.code || "",
                        bankId: selectedBankId as number,
                      });
                    }
                  }}
                  label="Банк (внутрибанк)"
                >
                  <MenuItem value="">Любой банк</MenuItem>
                  {banks.map((bank) => (
                    <MenuItem key={bank.id} value={bank.id}>
                      {bank.name} ({bank.code})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth margin="normal">
                <InputLabel>Метод оплаты</InputLabel>
                <Select
                  value={formData.method}
                  onChange={(e) =>
                    setFormData({ ...formData, method: e.target.value })
                  }
                  label="Метод оплаты"
                >
                  <MenuItem value="">Авто (по умолчанию)</MenuItem>
                  {paymentMethods
                    .filter((m) => m.isActive)
                    .map((m) => (
                      <MenuItem key={m.method} value={m.method}>
                        {m.displayName}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>



              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isSbp}
                    onChange={(e) =>
                      setFormData({ ...formData, isSbp: e.target.checked })
                    }
                  />
                }
                label="SBP Payment"
              />


            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                Fee Calculation
              </Typography>

              <Card>
                <CardContent>
                  {(() => {
                    const fees = calculateFees(formData.amount, formData.type);
                    return (
                      <>
                        <Typography variant="body2" color="textSecondary">
                          Amount: {formData.amount} {formData.currency}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          Merchant Fee (2%): {fees.merchantFee.toFixed(2)}{" "}
                          {formData.currency}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          Provider Fee (1%): {fees.providerFee.toFixed(2)}{" "}
                          {formData.currency}
                        </Typography>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="h6">
                          Total Fee: {fees.totalFee.toFixed(2)}{" "}
                          {formData.currency}
                        </Typography>
                        <Typography variant="h6" color="primary">
                          Net Amount: {fees.netAmount.toFixed(2)}{" "}
                          {formData.currency}
                        </Typography>
                      </>
                    );
                  })()}
                </CardContent>
              </Card>

              <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                Exchange Rates
              </Typography>
              <Card>
                <CardContent>
                  <Typography variant="body2">
                    USD/RUB: {exchangeRates.usdToRub || "N/A"}
                  </Typography>
                  <Typography variant="body2">
                    EUR/RUB: {exchangeRates.eurToRub || "N/A"}
                  </Typography>
                  <Typography variant="body2">
                    USDT/RUB: {exchangeRates.usdtToRub || "N/A"}
                  </Typography>
                </CardContent>
              </Card>

              <Button
                variant="contained"
                fullWidth
                onClick={handleCreateOrder}
                disabled={loading}
                sx={{ mt: 3 }}
              >
                Create Order
              </Button>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Typography variant="h6" gutterBottom>
            Merchant API Test
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="API Key"
                value={apiTestData.apiKey}
                onChange={(e) =>
                  setApiTestData({ ...apiTestData, apiKey: e.target.value })
                }
                margin="normal"
                placeholder="Enter merchant API key"
              />

              <TextField
                fullWidth
                label="Order ID"
                value={apiTestData.invId}
                onChange={(e) =>
                  setApiTestData({ ...apiTestData, invId: e.target.value })
                }
                margin="normal"
              />

              <TextField
                fullWidth
                label="Amount"
                type="number"
                value={apiTestData.amount}
                onChange={(e) =>
                  setApiTestData({
                    ...apiTestData,
                    amount: parseFloat(e.target.value),
                  })
                }
                margin="normal"
              />

              <FormControl fullWidth margin="normal">
                <InputLabel>Банк (внутрибанк)</InputLabel>
                <Select
                  value={apiTestData.bankId || ""}
                  onChange={(e) => {
                    const selectedBankId = e.target.value as number | "";
                    if (selectedBankId === "") {
                      setApiTestData({ ...apiTestData, bank: "", bankId: null });
                    } else {
                      const selectedBank = banks.find(b => b.id === selectedBankId);
                      setApiTestData({
                        ...apiTestData,
                        bank: selectedBank?.code || "",
                        bankId: selectedBankId as number,
                      });
                    }
                  }}
                  label="Банк (внутрибанк)"
                >
                  <MenuItem value="">Любой банк</MenuItem>
                  {banks.map((bank) => (
                    <MenuItem key={bank.id} value={bank.id}>
                      {bank.name} ({bank.code})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth margin="normal">
                <InputLabel>Метод оплаты</InputLabel>
                <Select
                  value={apiTestData.method}
                  onChange={(e) =>
                    setApiTestData({ ...apiTestData, method: e.target.value })
                  }
                  label="Метод оплаты"
                >
                  <MenuItem value="">Авто</MenuItem>
                  {paymentMethods
                    .filter((m) => m.isActive)
                    .map((m) => (
                      <MenuItem key={m.method} value={m.method}>
                        {m.displayName}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>

              <Button
                variant="contained"
                fullWidth
                onClick={handleApiTest}
                disabled={loading}
                sx={{ mt: 2 }}
              >
                Test API Create Order
              </Button>
            </Grid>

            <Grid item xs={12} md={6}>
              <Alert severity="info">
                <Typography variant="body2">
                  API Endpoint: POST /api/merchant/create-order
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Headers: X-API-Key: your-api-key
                </Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Body: {`{ invId, amount, currency, bank }`}
                </Typography>
              </Alert>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <DataGrid
            rows={orders}
            columns={columns}
            initialState={{
              pagination: {
                paginationModel: { pageSize: 10 },
              },
            }}
            pageSizeOptions={[10, 25, 50]}
            autoHeight
            onRowClick={(params) => setSelectedOrder(params.row)}
          />
        </TabPanel>
      </Paper>

      <Dialog
        open={!!selectedOrder}
        onClose={() => setSelectedOrder(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Order Details</DialogTitle>
        <DialogContent>
          {selectedOrder && (
            <Box>
              <Typography variant="body2">
                <strong>ID:</strong> {selectedOrder.id}
              </Typography>
              <Typography variant="body2">
                <strong>Invoice ID:</strong> {selectedOrder.invId}
              </Typography>
              <Typography variant="body2">
                <strong>Status:</strong> {selectedOrder.status}
              </Typography>
              <Typography variant="body2">
                <strong>Type:</strong> {selectedOrder.type}
              </Typography>
              <Typography variant="body2">
                <strong>Amount:</strong> {selectedOrder.amount}{" "}
                {selectedOrder.currency}
              </Typography>
              <Typography variant="body2">
                <strong>Merchant Fee:</strong> {selectedOrder.merchantFee}
              </Typography>
              <Typography variant="body2">
                <strong>Provider Fee:</strong> {selectedOrder.providerFee}
              </Typography>
              <Typography variant="body2">
                <strong>Provider:</strong>{" "}
                {selectedOrder.provider?.name || "Not assigned"}
              </Typography>
              <Typography variant="body2">
                <strong>Created:</strong>{" "}
                {selectedOrder.createdAt &&
                  format(
                    new Date(selectedOrder.createdAt),
                    "dd.MM.yyyy HH:mm:ss"
                  )}
              </Typography>

              {selectedOrder.metadata && (
                <>
                  <Typography variant="h6" sx={{ mt: 2 }}>
                    Metadata
                  </Typography>
                  <pre>{JSON.stringify(selectedOrder.metadata, null, 2)}</pre>
                </>
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default Orders;
